import { useState, useEffect } from 'react';
import { useSendSmsMutation, useSmsCertificateMutation, useGetSendSmsMessageQuery, useGetAccountInfoQuery } from 'views/utils/graphql/graphql';
import { useFormik } from 'formik';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useToast } from 'views/mutual/toast/hooks';
import { SendSMSParam } from 'views/templates/sendSMS';
import { ToastMessageLevel } from 'views/mutual/toast';
import { useAuthGuard } from 'views/auth/useAuth';

export enum PageStep {
  PRE,
  INITIAL,
  AUTH,
  QUESTION,
  UPDATE,
  CONFIRM,
  SENDMAIL,
  COMPLETE,
}

export const useAccountInfo = () => {
  const [pageStep, setPageStep] = useState<PageStep>(PageStep.PRE);
  const [provisionalToken, setProvisionalToken] = useState<string>('');
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>('');

  const [fpgPolicy, setFpgPolicy] = useState<boolean>(false);
  const [fpgSecPolicy, setFpgSecPolicy] = useState<boolean>(false);
  const [fpgTrustPolicy, setFpgTrustPolicy] = useState<boolean>(false);

  const { toastProps, showToast } = useToast();
  const { onSendSMS } = useSendSMS(setProvisionalToken, pageStep, setPageStep, showToast, newPhoneNumber);
  const initislPageProps = useInitialPage(onSendSMS);
  const authPageProps = useAuthPage(provisionalToken, newPhoneNumber, setPageStep, showToast);
  const { isLogin } = useAuthGuard();
  const [accountInfo, _] = useGetAccountInfoQuery({
    pause: !isLogin,
    requestPolicy: 'network-only',
  });
  // const updateConfirmProps = useUpdateConfirm(setPageStep, updatePageProps.setSelected, setNewPhoneNumber, showToast);
  const fpgPolicyProps = {
    checked: fpgPolicy,
    setChecked: (value: boolean) => setFpgPolicy(value),
  };

  const fpgSecPolicyProps = {
    checked: fpgSecPolicy,
    setChecked: (value: boolean) => setFpgSecPolicy(value),
  };

  const fpgTrustPolicyProps = {
    checked: fpgTrustPolicy,
    setChecked: (value: boolean) => setFpgTrustPolicy(value),
  };

  const agreementProps = {
    disable: !fpgPolicy || !fpgSecPolicy || !fpgTrustPolicy,
    onClick: () => {
      setPageStep(PageStep.INITIAL);
    },
  };

  useEffect(() => {
    setPageStep(PageStep.PRE);
    setProvisionalToken('');
    setNewPhoneNumber('');
  }, []);

  return {
    pageStep,
    setPageStep,
    accountInfo,
    initislPageProps,
    authPageProps: {
      ...authPageProps,
      onSendSMS,
    },
    // updatePageProps,
    // updateConfirmProps,
    fpgPolicyProps,
    fpgSecPolicyProps,
    fpgTrustPolicyProps,
    agreementProps,
    toastProps,
  };
};

//認証コード送信
const useSendSMS = (
  setProvisionalToken: React.Dispatch<React.SetStateAction<string>>,
  pageStep: PageStep,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
  onShowToast: (label: string, messageLevel: ToastMessageLevel) => void,
  newPhoneNumber: string,
) => {
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const isNotNewPhoneNumber = newPhoneNumber === '';
        const res = await sendSMS({
          input: {
            Type: isNotNewPhoneNumber ? 2 : 4,
            PhoneNumber: isNotNewPhoneNumber ? undefined : newPhoneNumber,
          },
        });
        if (res.data && res.data.SendSMS?.Code !== 0) {
          onShowToast(res.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          setPageStep(PageStep.AUTH);
          if (pageStep === PageStep.AUTH) {
            onShowToast('認証コードを送信しました', ToastMessageLevel.Info);
          }
        }
      },
    },
    [sendSMS, setProvisionalToken, pageStep, setPageStep, onShowToast, newPhoneNumber],
  );

  return {
    onSendSMS,
  };
};

//初期ページ
const useInitialPage = (onSendSMS: () => Promise<void>) => {
  const sendSMSBtnProps = {
    onClick: async () => await onSendSMS(),
  };
  return {
    sendSMSBtnProps,
  };
};

//認証ページ
const useAuthPage = (
  provisionalToken: string,
  newPhoneNumber: string,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [accountInfo, _] = useGetSendSmsMessageQuery({
      requestPolicy: 'network-only',
  });
  const [smsCertificatelData, smsCertificate] = useSmsCertificateMutation();
  const isNotNewPhoneNumber = newPhoneNumber === '';

  let text : string = '';
  let phoneNumber : string | undefined;

  if(isNotNewPhoneNumber){
    text = accountInfo.data?.GetAccountInfo?.SendSMSMessage || '送信された認証コードを入力してください';
  }
  else{
    phoneNumber = newPhoneNumber.slice(-4);
  }

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const onSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await smsCertificate({
          input: {
            Type: 2,
            AuthenticationCode: values.authenticationCode,
            ProvisionalToken: provisionalToken,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data?.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.SMSCertificate?.Code === 0) {
          isNotNewPhoneNumber ? setPageStep(PageStep.QUESTION) : setPageStep(PageStep.SENDMAIL);
        }
      },
    },
    [provisionalToken, showToast, isNotNewPhoneNumber, setPageStep],
  );

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setFieldValue('authenticationCode', '');
  }, []);

  return { formik, text, phoneNumber, isUpdate: true };
};
